import { type CSSProperties } from 'react'
import { Link } from 'react-router-dom'

import { useAuth } from '../../contexts/AuthContext'
import { type Demo } from '../../Root'
import { isValidHex } from './utils'

export interface DemoCardProps {
  demo: Demo
  onDemoClick: (clientName: string) => void
  onEdit: (demo: Demo) => void
  onUpdated: () => Promise<void>
}

export const DemoCard = ({
  demo,
  onDemoClick,
  onEdit,
  onUpdated,
}: DemoCardProps) => {
  const { authFetch } = useAuth()

  const updateDemo = async (body: object) => {
    const response = await authFetch(`/api/demos/${demo.clientName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.error(response)
      return
    }
    await onUpdated()
  }

  const toggleArchived = () => {
    if (!demo.archivedAt && !window.confirm(`Archive ${demo.clientName}? Its login stops working and open sessions end.`)) {
      return
    }
    void updateDemo({ archived: !demo.archivedAt })
  }

  const cardStyle: CSSProperties = {
    width: '300px',
    minHeight: '430px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '10px',
    margin: '10px',
    border: '2px solid #000',
    borderRadius: '10px',
    boxSizing: 'border-box',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
  }

  const imageStyle: CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100px',
    minHeight: '100px',
    objectFit: 'contain',
  }

  const colorBlockStyleLight: CSSProperties = {
    width: '100%',
    height: '50px',
    backgroundColor:
      demo.brandColorLightHex && isValidHex(demo.brandColorLightHex)
        ? demo.brandColorLightHex
        : '#FFFFFF',
    textAlign: 'center',
    lineHeight: '50px',
    color: '#000000',
  }

  const colorBlockStyleDark: CSSProperties = {
    width: '100%',
    height: '50px',
    backgroundColor:
      demo.brandColorDarkHex && isValidHex(demo.brandColorDarkHex)
        ? demo.brandColorDarkHex
        : '#000000',
    textAlign: 'center',
    lineHeight: '50px',
    color: '#FFFFFF',
  }

  return (
    <div style={cardStyle}>
      <Link to={`/${demo.clientName}`} onClick={() => onDemoClick(demo.clientName)}>
        <div>
          <p>Name: {demo.clientName}</p>
          <img
            src={demo.brandLogoUrl}
            alt={`${demo.clientName} brand logo`}
            style={imageStyle}
          />
          <div style={colorBlockStyleLight}>
            {isValidHex(demo.brandColorLightHex)
              ? 'Light Brand Color'
              : 'No Color'}
          </div>
          <div style={colorBlockStyleDark}>
            {isValidHex(demo.brandColorDarkHex)
              ? 'Dark Brand Color'
              : 'No Color'}
          </div>
          <p>Demo business_id: {demo.businessId ? demo.businessId : 'N/A'}</p>
          <p>
            Has credential override:{' '}
            {demo.hasCredentialOverride ? 'Yes' : 'No'}
          </p>
        </div>
      </Link>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className='admin-button' onClick={() => onEdit(demo)}>
          Edit
        </button>
        <button className='admin-button' onClick={() => updateDemo({ businessId: null })}>
          Reset
        </button>
        <button className='admin-button' onClick={toggleArchived}>
          {demo.archivedAt ? 'Unarchive' : 'Archive'}
        </button>
      </div>
    </div>
  )
}
